import { Accordion, AccordionItem } from '@carbon/react';
import classNames from 'classnames';
import { SplitPanel } from 'components/split-panel';
import { Geometry } from 'geojson';
import { useAtom, useAtomValue } from 'jotai';
import { isNil, round } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { SlotCategory } from 'types/evaluation';
import {
  createdSubtractDetailsLoadableAtom,
  createdSubtractGeometryLoadableAtom,
  tractIntersectionAnalysisUatThresholdAtom,
  wellboreExclusionsLoadableAtom,
} from '../atoms';
import {
  useNRIReviewData,
  useTractIntersectionRequestAnalysis,
  useTractWorkflow,
} from '../hooks';
import { ManageSlotsModal } from '../manage-slots-modal/manage-slots-modal';
import { TractDetails } from '../tract-details';
import { coverageThresholdOptions, sortUnitsByDevelopmentType } from '../utils';
import { AllocationManagerAccordion } from '../allocation-manager-accordion';
import { ParticipationFactorDropdown } from '../participation-factor-dropdown';
import { WorkflowStatusPopover } from '../workflow-status-popover';
import {
  ScopedMapPanel,
  ThresholdOverlay,
} from '../scoped-map-panel/scoped-map-panel';
import { CoverageThreshold } from './coverage-threshold';
import style from './current-development.module.scss';
import { SkeletonList } from './skeleton-list';
import { SlotList } from './slot-list';
import { SlotSummaryTable } from 'components/slot-summary-table';
import { UatList } from './uat-list';
import { UnitList } from './unit-list';
import { wellStatusCounts } from 'utils/slot-categories';
interface CurrentDevelopmentProps {
  tractId?: string | null;
}

export const CurrentDevelopment = ({
  // tractId is set at the wizard level via editTractIdAtom
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tractId,
}: CurrentDevelopmentProps) => {
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(
    undefined
  );

  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>(
    undefined
  );

  const [selectedUatId, setSelectedUatId] = useState<number | undefined>(
    undefined
  );

  // Which threshold overlay is active on the map: 'unit', 'uat', or null
  const [activeThresholdView, setActiveThresholdView] = useState<
    'unit' | 'uat' | null
  >('unit');

  const [
    tractIntersectionAnalysisUatThreshold,
    setTractIntersectionAnalysisUatThreshold,
  ] = useAtom(tractIntersectionAnalysisUatThresholdAtom);

  const {
    currentDevelopmentTractIntersectionAnalysis,
    isLoading,
    retriggerTractIntersectionAnalysis,
  } = useTractIntersectionRequestAnalysis();
  const selectedUnitName =
    currentDevelopmentTractIntersectionAnalysis?.units?.find(
      (unit) => unit.id === selectedUnitId
    )?.name;

  // Use the workflow hook for Pusher subscriptions and state management
  const { workflowStatus } = useTractWorkflow();

  // NRI data from slot_allocations — used for Interest Info cards on slots/UATs.
  // This is the live data source that updates when PTC changes (via refetchNriDataAtom).
  const { slotNRI } = useNRIReviewData();

  // Lookup map: well UWI → slot-level NRI data (for SlotInterestInfo cards)
  const slotNriByUwi = useMemo(() => {
    if (!slotNRI) return new Map();
    const map = new Map();
    for (const row of slotNRI) {
      if (row.slotId) {
        map.set(row.slotId, row);
      }
    }
    return map;
  }, [slotNRI]);

  const defaultThreshold = useMemo(() => {
    return {
      unitSelectedThreshold:
        currentDevelopmentTractIntersectionAnalysis?.parameters
          ?.unitCoverageThreshold ?? 0,
      uatSelectedThreshold:
        currentDevelopmentTractIntersectionAnalysis?.parameters
          ?.uatCoverageThreshold ?? 0,
    };
  }, [
    currentDevelopmentTractIntersectionAnalysis?.parameters
      ?.unitCoverageThreshold,
    currentDevelopmentTractIntersectionAnalysis?.parameters
      ?.uatCoverageThreshold,
  ]);

  const uatUnits = useMemo(() => {
    const getUatsFromUnits = (
      units:
        | Array<{
            unitName: string;
            uats: Array<{ id: number; geometry: Geometry }>;
          }>
        | undefined
    ) =>
      (units ?? [])
        .filter((unit) => unit.unitName === selectedUnitName)
        .flatMap((unit) => unit.uats);

    return [
      ...getUatsFromUnits(
        currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
          ?.intersecting
      ).map((uat) => ({
        id: uat.id.toString(),
        geometry: uat.geometry,
        isAboveThreshold: true,
      })),
      ...getUatsFromUnits(
        currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
          ?.filtered
      ).map((uat) => ({
        id: uat.id.toString(),
        geometry: uat.geometry,
        isAboveThreshold: false,
      })),
    ];
  }, [
    currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts,
    selectedUnitName,
  ]);

  const wellboreExclusionsLoadable = useAtomValue(
    wellboreExclusionsLoadableAtom
  );
  const wellboreExclusionsByUwi = useMemo(() => {
    const map = new Map<string, string | null>();
    if (wellboreExclusionsLoadable.state === 'hasData') {
      wellboreExclusionsLoadable.data.forEach((e) => map.set(e.uwi, e.reason));
    }
    return map;
  }, [wellboreExclusionsLoadable]);

  // Slots displayed for the selected unit. Includes both intersecting wells
  // (potentially with isWellboreExcluded marker if the user has excluded one
  // since the analysis ran) AND wells already in the excluded_by_wellbore
  // bucket from the server response. Excluded slots remain visible but are
  // marked as unvalued — never hidden, per the design spec.
  const slotsByUnit = useMemo(() => {
    const wells = currentDevelopmentTractIntersectionAnalysis?.wells;
    if (!wells) return [];

    const intersectingForUnit = (wells.intersecting ?? []).filter(
      (well) => well.unitName === selectedUnitName
    );
    const excludedForUnit = (wells.excludedByWellbore ?? []).filter(
      (well) => well.unitName === selectedUnitName
    );

    return [
      ...intersectingForUnit.map((w) => ({
        ...w,
        isWellboreExcluded: wellboreExclusionsByUwi.has(w.uwi),
        wellboreExclusionReason:
          wellboreExclusionsByUwi.get(w.uwi) ?? undefined,
      })),
      ...excludedForUnit.map((w) => ({
        ...w,
        isWellboreExcluded: true,
        wellboreExclusionReason: w.wellboreExclusionReason,
      })),
    ];
  }, [
    currentDevelopmentTractIntersectionAnalysis?.wells,
    selectedUnitName,
    wellboreExclusionsByUwi,
  ]);

  // Allocation Manager ID for the selected slot — from TIA response
  const selectedSlotAllocationManagerId = useMemo(() => {
    if (!selectedSlotId) return null;
    const well = slotsByUnit?.find((w) => w.uwi === selectedSlotId);
    return well?.allocationManagerId ?? null;
  }, [selectedSlotId, slotsByUnit]);

  const [isManageSlotsOpen, setIsManageSlotsOpen] = useState(false);

  // Set of slot UWIs that have at least one UAT in the selected unit.
  // Slots in this set show read-only rollup verification; slots NOT in
  // it render interactive verification controls on the interest card.
  const slotsWithUats = useMemo(() => {
    const set = new Set<string>();
    const intersecting =
      currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
        ?.intersecting ?? [];
    for (const unit of intersecting) {
      if (unit.unitName !== selectedUnitName) continue;
      for (const uat of unit.uats) {
        for (const uwi of uat.wellUwis) {
          set.add(uwi);
        }
      }
    }
    return set;
  }, [
    currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts
      ?.intersecting,
    selectedUnitName,
  ]);

  const uatsBySlot = useMemo(
    () =>
      currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts?.intersecting
        ?.filter(
          (intersectingUat) => intersectingUat.unitName === selectedUnitName
        )
        .map((unit) =>
          unit.uats.filter((uat) => uat.wellUwis.includes(selectedSlotId ?? ''))
        ),
    [
      currentDevelopmentTractIntersectionAnalysis?.unitAllocationTracts,
      selectedSlotId,
      selectedUnitName,
    ]
  );

  const sortedUnits = useMemo(() => {
    if (!currentDevelopmentTractIntersectionAnalysis?.units) return [];
    return sortUnitsByDevelopmentType(
      currentDevelopmentTractIntersectionAnalysis.units
    );
  }, [currentDevelopmentTractIntersectionAnalysis?.units]);

  // Build threshold items for the map overlay
  const unitThresholdItems = useMemo(() => {
    const included = (
      currentDevelopmentTractIntersectionAnalysis?.units ?? []
    ).map((u) => ({
      id: u.id,
      geometry: u.geometry,
      isAboveThreshold: true,
    }));
    const excluded = (
      currentDevelopmentTractIntersectionAnalysis?.excludedUnits ?? []
    ).map((u) => ({
      id: u.id,
      geometry: u.geometry,
      isAboveThreshold: false,
    }));
    return [...included, ...excluded].filter((item) => item.geometry);
  }, [
    currentDevelopmentTractIntersectionAnalysis?.units,
    currentDevelopmentTractIntersectionAnalysis?.excludedUnits,
  ]);

  const uatThresholdItems = useMemo(() => {
    return uatUnits.filter((item) => item.geometry);
  }, [uatUnits]);

  const thresholdMapItems = useMemo(() => {
    if (activeThresholdView === 'unit') return unitThresholdItems;
    if (activeThresholdView === 'uat') return uatThresholdItems;
    return [];
  }, [activeThresholdView, unitThresholdItems, uatThresholdItems]);

  // Auto-select first unit when data loads
  useEffect(() => {
    if (sortedUnits.length > 0 && !selectedUnitId) {
      setSelectedUnitId(sortedUnits[0].id);
    }
  }, [sortedUnits, selectedUnitId]);

  // --- Scoped map data wiring ---

  const selectedUnitGeometry = useMemo(() => {
    if (!selectedUnitId) return null;
    const unit = sortedUnits.find((u) => u.id === selectedUnitId);
    return (unit?.geometry as GeoJSON.Polygon) ?? null;
  }, [sortedUnits, selectedUnitId]);

  // The acreage basis is stored per (subtract, UAT), so the cards need the
  // owning subtract to address the endpoint.
  const subtractDetailsLoadable = useAtomValue(
    createdSubtractDetailsLoadableAtom
  );
  const subtractId =
    subtractDetailsLoadable.state === 'hasData'
      ? subtractDetailsLoadable.data?.id
      : undefined;

  const subtractGeometryLoadable = useAtomValue(
    createdSubtractGeometryLoadableAtom
  );
  const subtractGeometry =
    subtractGeometryLoadable.state === 'hasData'
      ? (subtractGeometryLoadable.data?.geojson ?? null)
      : null;

  // Well features — derived from TIA response (survey/stick geometry
  // included in the current_development endpoint, no separate fetch needed)
  const wellFeatures = useMemo(
    () =>
      slotsByUnit.flatMap((w) => {
        if (!w.wellGeometry) return [];
        const feature: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          geometry: w.wellGeometry,
          properties: {},
        };
        return [{ uwi: w.uwi, feature }];
      }),
    [slotsByUnit]
  );

  const scopedUatGeometries = useMemo(
    () =>
      uatUnits
        .filter((u) => u.geometry)
        .map((u) => ({ id: Number(u.id), geometry: u.geometry })),
    [uatUnits]
  );

  const thresholdOverlay = useMemo((): ThresholdOverlay | undefined => {
    if (!activeThresholdView || thresholdMapItems.length === 0)
      return undefined;
    return { type: activeThresholdView, items: thresholdMapItems };
  }, [activeThresholdView, thresholdMapItems]);

  return (
    <SplitPanel
      left={
        <div className={style.summaryContainer}>
          <ManageSlotsModal
            isOpen={isManageSlotsOpen}
            onClose={() => setIsManageSlotsOpen(false)}
            wells={currentDevelopmentTractIntersectionAnalysis?.wells}
          />
          <h2 className={style.summaryTitle}>Current Development</h2>
          <Accordion>
            <AccordionItem
              className={style.accordionContainer}
              title={
                <div className={style.tractDetailsTitleContainer}>
                  <span className={style.tractDetailsTitle}>
                    Tract & Subtract Details
                  </span>
                  <WorkflowStatusPopover
                    statusItems={workflowStatus.statusItems}
                    overallStatus={workflowStatus.overallStatus}
                  />
                </div>
              }
              open={true}
            >
              <div className={style.linkedItemSectionContainer}>
                <TractDetails />
              </div>
            </AccordionItem>
            <AccordionItem
              className={style.accordionContainer}
              title={
                <span className={style.tractDetailsTitle}>Slot Summary</span>
              }
              open={true}
            >
              <SlotSummaryTable
                slotSummary={
                  currentDevelopmentTractIntersectionAnalysis?.slotsSummary?.rows?.map(
                    (row) => {
                      return {
                        total: row.total,
                        wellType: row.unitType,
                        producing: row.pdp,
                        duc: row.duc,
                        permit: row.permit,
                        ltd: row.ltd,
                        shutIn: row.shutIn,
                        inactive: row.inactive,
                        ...wellStatusCounts(row),
                        expiredPermit: row.expiredPermit,
                        wellboreExcluded: row.wellboreExcluded ?? 0,
                      };
                    }
                  ) ?? []
                }
                isLoading={isLoading}
                tableId="crm-current-development-slot-summary"
              />
            </AccordionItem>
            <AccordionItem
              title={
                <span className={style.tractDetailsTitle}>
                  Current Development Configuration
                </span>
              }
              className={style.accordionContainer}
              open={true}
            >
              <div className={style.intersectionContainer}>
                <div className={style.converageContainer}>
                  <CoverageThreshold
                    title="Unit Coverage Threshold"
                    description="The Unit Coverage Threshold determines which Units are recognized based on their overlap to the Subtract. Units with an overlap to the Subtract higher than the selected threshold are classified as intersecting, while those below this threshold are ignored. The default threshold is 3%."
                    instructions="Select a threshold to determine which Units are recognized as intersecting. Lower values include more Units with minor overlaps, while higher values filter them out. Green: Included | Red: Excluded"
                    thresholdOptions={coverageThresholdOptions.map(
                      (thresholdOption) => {
                        const isSelected =
                          defaultThreshold.unitSelectedThreshold * 100 ===
                          thresholdOption.value;
                        return {
                          ...thresholdOption,
                          isSelected,
                        };
                      }
                    )}
                    isLoading={isLoading}
                    isMapActive={activeThresholdView === 'unit'}
                    onShowOnMap={() => {
                      setActiveThresholdView((prev) => {
                        return prev === 'unit' ? null : 'unit';
                      });
                    }}
                    onThresholdChange={(threshold) => {
                      retriggerTractIntersectionAnalysis({
                        unitCoverageThreshold: threshold / 100,
                      });
                    }}
                  />
                  <CoverageThreshold
                    title="UAT Coverage Threshold"
                    description="The UAT Coverage Threshold determines which UATs are recognized based on their overlap to the Subtract. UATs with an overlap to the Subtract higher than the selected threshold are classified as intersecting, while those below this threshold are ignored. The default threshold is 3%."
                    instructions="Select a threshold to determine which Units are recognized as intersecting. Lower values include more Units with minor overlaps, while higher values filter them out. Green: Included | Red: Excluded"
                    thresholdOptions={coverageThresholdOptions.map(
                      (threshold) => {
                        const uatThreshold =
                          tractIntersectionAnalysisUatThreshold.find(
                            (t) => t.unitId === selectedUnitId
                          )?.threshold ?? defaultThreshold.uatSelectedThreshold;
                        return {
                          ...threshold,
                          isSelected:
                            uatUnits.length > 0
                              ? uatThreshold * 100 === threshold.value
                              : false,
                        };
                      }
                    )}
                    isLoading={isLoading}
                    isMapActive={activeThresholdView === 'uat'}
                    onShowOnMap={() => {
                      setActiveThresholdView((prev) => {
                        return prev === 'uat' ? null : 'uat';
                      });
                    }}
                    onThresholdChange={(threshold) => {
                      batchedUpdates(() => {
                        setTractIntersectionAnalysisUatThreshold(
                          (thresholds) => {
                            const newThresholds = [
                              ...thresholds.filter(
                                (t) => t.unitId !== selectedUnitId
                              ),
                              {
                                unitId: selectedUnitId ?? '',
                                threshold: threshold / 100,
                              },
                            ];
                            return newThresholds;
                          }
                        );

                        retriggerTractIntersectionAnalysis({
                          uatCoverageThreshold: threshold / 100,
                          unitId: selectedUnitId ?? '',
                        });
                      });
                    }}
                  />
                  <ParticipationFactorDropdown />
                </div>
                <div className={style.listContainer}>
                  <div className={style.listItemContainer}>
                    <div
                      className={classNames(style.listItem, style.unitListItem)}
                    >
                      {isLoading ? (
                        <SkeletonList title="Units" rows={3} />
                      ) : (
                        <UnitList
                          units={sortedUnits.map((unit) => {
                            const wellsForUnit = (
                              currentDevelopmentTractIntersectionAnalysis?.wells
                                ?.intersecting ?? []
                            ).filter((w) => w.unitName === unit.name);
                            const pdpCount = wellsForUnit.filter(
                              (w) =>
                                w.status?.toLowerCase() === 'producing' ||
                                w.status?.toLowerCase() === 'pdp'
                            ).length;
                            return {
                              areaAc: unit.acreage
                                ? round(unit.acreage, 2)
                                : undefined,
                              source: unit.source,
                              ltdCapable: unit.ltdCapable,
                              type: unit.unitDevelopmentType,
                              unitAgreementType: unit.unitAgreementType,
                              id: unit.id,
                              name: unit.name,
                              geometry: unit.geometry,
                              href: unit.href,
                              slotCount: wellsForUnit.length,
                              pdpCount,
                              subtractToUnitPct: isNil(
                                unit.coverage?.tractToUnit
                              )
                                ? undefined
                                : round(unit.coverage?.tractToUnit * 100, 2),
                              unitToSubtractPct: isNil(
                                unit.coverage?.unitToTract
                              )
                                ? undefined
                                : round(unit.coverage?.unitToTract * 100, 2),
                            };
                          })}
                          selectedUnitId={selectedUnitId}
                          setSelectedUnitId={(unitId) => {
                            setSelectedUnitId(unitId);
                            if (selectedUnitId !== unitId) {
                              setSelectedSlotId(undefined);
                              setSelectedUatId(undefined);
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={style.listItem}>
                      {isLoading ? (
                        <SkeletonList title="Slots" rows={2} />
                      ) : (
                        <SlotList
                          onManageSlots={() => setIsManageSlotsOpen(true)}
                          slotsWithUats={slotsWithUats}
                          slots={
                            slotsByUnit?.map((well) => {
                              return {
                                id: well.uwi,
                                name: well.leaseName,
                                apiNumber: well.href,
                                status: well.status as SlotCategory,
                                operator: well.currentOperator,
                                unitId: well.unitName,
                                allocationMethod: well.allocationMethod,
                                isWellboreExcluded: well.isWellboreExcluded,
                                wellboreExclusionReason:
                                  well.wellboreExclusionReason,
                              };
                            }) ?? []
                          }
                          slotNriByUwi={slotNriByUwi}
                          selectedSlotId={selectedSlotId}
                          setSelectedSlotId={(uwi) => {
                            setSelectedSlotId(uwi);
                            if (selectedSlotId !== uwi) {
                              setSelectedUatId(undefined);
                            }
                          }}
                        />
                      )}
                    </div>
                    <div className={style.listItem}>
                      {isLoading ? (
                        <SkeletonList title="UATs" rows={1} />
                      ) : (
                        <UatList
                          uats={
                            uatsBySlot?.flat().map((uatBySlot) => {
                              return {
                                id: uatBySlot.id,
                                name: uatBySlot.tractName,
                                allocationValue: uatBySlot.allocationValue,
                                allocationFactor: round(
                                  uatBySlot.allocationFactor,
                                  2
                                ),
                                subtractToUatPct: round(
                                  uatBySlot.subtractCoverage.subtractToUat *
                                    100,
                                  2
                                ),
                                uatToSubtractPct: round(
                                  uatBySlot.subtractCoverage.uatToSubtract *
                                    100,
                                  2
                                ),
                                gisUuid: uatBySlot.gisUuid,
                                acreageBasis: uatBySlot.acreageBasis,
                              };
                            }) ?? []
                          }
                          subtractId={subtractId}
                          slotNriByUwi={slotNriByUwi}
                          selectedSlotId={selectedSlotId}
                          selectedUatId={selectedUatId}
                          setSelectedUatId={(uatId) => {
                            batchedUpdates(() => {
                              setSelectedUatId(uatId);
                            });
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      }
      right={
        <ScopedMapPanel
          selectedUnitGeometry={selectedUnitGeometry}
          wellFeatures={wellFeatures}
          uatGeometries={scopedUatGeometries}
          subtractGeometry={subtractGeometry}
          selectedSlotId={selectedSlotId}
          selectedUatId={selectedUatId}
          thresholdOverlay={thresholdOverlay}
          allocationManager={
            <AllocationManagerAccordion
              allocationManagerId={selectedSlotAllocationManagerId}
            />
          }
        />
      }
    />
  );
};
